import uniq from "lodash-es/uniq";
import { Reducer } from "redux";

import {
    LinkedObjects,
    STROLLERS_DETAIL_ADD_TO_PROFILE_API_FAILURE,
    STROLLERS_DETAIL_ADD_TO_PROFILE_API_REQUEST,
    STROLLERS_DETAIL_ADD_TO_PROFILE_API_SUCCESS,
    STROLLERS_DETAIL_CHANGE_VARIANT_FEATURE_VALUE_SUCCESS,
    STROLLERS_DETAIL_CHANGE_VARIANT_FEATURE_VALUE_TRIGGER,
    STROLLERS_DETAIL_FETCH_FAILURE,
    STROLLERS_DETAIL_FETCH_REQUEST,
    STROLLERS_DETAIL_FETCH_SUCCESS,
    STROLLERS_DETAIL_LOAD_MORE_SIMILARS_FAILURE,
    STROLLERS_DETAIL_LOAD_MORE_SIMILARS_REQUEST,
    STROLLERS_DETAIL_LOAD_MORE_SIMILARS_SUCCESS,
    STROLLERS_DETAIL_REMOVE_FROM_PROFILE_API_FAILURE,
    STROLLERS_DETAIL_REMOVE_FROM_PROFILE_API_REQUEST,
    STROLLERS_DETAIL_REMOVE_FROM_PROFILE_API_SUCCESS,
    StrollersDetailAddToProfileApiFailureAction,
    StrollersDetailAddToProfileApiRequestAction,
    StrollersDetailAddToProfileApiSuccessAction,
    StrollersDetailChangeVariantFeatureValueSuccessAction,
    StrollersDetailChangeVariantFeatureValueTriggerAction,
    StrollersDetailFetchFailureAction,
    StrollersDetailFetchRequestAction,
    StrollersDetailFetchSuccessAction,
    StrollersDetailLoadMoreSimilarsFailureAction,
    StrollersDetailLoadMoreSimilarsRequestAction,
    StrollersDetailLoadMoreSimilarsSuccessAction,
    StrollersDetailRemoveFromProfileApiFailureAction,
    StrollersDetailRemoveFromProfileApiRequestAction,
    StrollersDetailRemoveFromProfileApiSuccessAction,
} from "@/mk2/apps/strollers/containers/StrollersDetail/StrollersDetail.actions";
import { VariantsOptionsEntity } from "@/mk2/apps/strollers/schemas";
import { AST, ASTAdditionalDataFromServer } from "@/mk2/apps/wiki/schemas";
import { tupdate } from "@/mk2/helpers/types";
import { errorMessage } from "@/mk2/reducers/errorMessage";
import { LoadingState, loadingState } from "@/mk2/reducers/loadingState";

export interface StrollerDetailState {
    similarIds: number[];
    strollerId: number;
    strollersOfThisBrandIds: number[];
    strollerQuestionIds: number[];
    strollerReviewIds: number[];
    strollerPendingReviewIds: number[];
    strollerAlikeStrollerIds: number[];
    counsellingId: number;
    loadingState: LoadingState;
    errorMessage: string;
    onProfile: boolean;
    addingToProfileLoadingState: LoadingState;
    removingFromProfileLoadingState: LoadingState;
    loadMoreSimilarsLoadingState: LoadingState;
    hasMoreSimilars: boolean;
    countOfSimilars: number;
    oldProductsCount: number;
    eshopProductsIds: number[];
    cheapestOldProductId: number;
    buyingGuideId: number;
    linkedObjects: LinkedObjects;
    articleIds: number[];
    patternsIds: number[];
    variantsIds: number[];
    variantsOptions: VariantsOptionsEntity;
    baseVariantId: number;
    strollerPhotosIds: number[];
    allStrollerPhotosIds: number[];
    variantsPhotos: Record<number, number[]>;
    relatedStrollersPreviewPhotos: Record<number, number>;
    descriptionAST: AST;
    descriptionASTAdditionalData: ASTAdditionalDataFromServer;
    descriptionPhotos: number[];
    eshopProductsWidgetIds: number[];
}

export const initialStrollerDetailState: StrollerDetailState = {
    loadingState: LoadingState.INIT,
    errorMessage: null,
    similarIds: [],
    strollerId: null,
    strollersOfThisBrandIds: [],
    strollerQuestionIds: [],
    strollerReviewIds: [],
    strollerPendingReviewIds: [],
    strollerAlikeStrollerIds: [],
    onProfile: false,
    addingToProfileLoadingState: LoadingState.INIT,
    removingFromProfileLoadingState: LoadingState.INIT,
    loadMoreSimilarsLoadingState: LoadingState.SUCCESS,
    hasMoreSimilars: false,
    countOfSimilars: null,
    cheapestOldProductId: null,
    eshopProductsIds: [],
    oldProductsCount: null,
    buyingGuideId: null,
    linkedObjects: [],
    counsellingId: null,
    articleIds: [],
    patternsIds: null,
    variantsIds: [],
    variantsOptions: {},
    baseVariantId: null,
    strollerPhotosIds: [],
    allStrollerPhotosIds: [],
    variantsPhotos: {},
    relatedStrollersPreviewPhotos: {},
    descriptionAST: null,
    descriptionASTAdditionalData: {},
    descriptionPhotos: [],
    eshopProductsWidgetIds: [],
};

export interface StrollersDetailState {
    [slugAndVariantId: string]: StrollerDetailState;
}

declare type StrollersDetailReducerAction =
    | StrollersDetailFetchRequestAction
    | StrollersDetailFetchSuccessAction
    | StrollersDetailFetchFailureAction
    | StrollersDetailLoadMoreSimilarsRequestAction
    | StrollersDetailLoadMoreSimilarsSuccessAction
    | StrollersDetailLoadMoreSimilarsFailureAction
    | StrollersDetailAddToProfileApiRequestAction
    | StrollersDetailAddToProfileApiSuccessAction
    | StrollersDetailAddToProfileApiFailureAction
    | StrollersDetailRemoveFromProfileApiRequestAction
    | StrollersDetailRemoveFromProfileApiSuccessAction
    | StrollersDetailRemoveFromProfileApiFailureAction
    | StrollersDetailChangeVariantFeatureValueTriggerAction
    | StrollersDetailChangeVariantFeatureValueSuccessAction;

function slugAndVariantId(action: StrollersDetailReducerAction): string {
    const slug = action.slug;
    const variantId = "variantId" in action && action.variantId;
    return variantId ? `${slug}-${variantId}` : slug;
}

export const strollersDetailReducer: Reducer<StrollersDetailState> = (
    state: StrollersDetailState = {},
    action: StrollersDetailReducerAction,
) => {
    switch (action.type) {
        case STROLLERS_DETAIL_FETCH_REQUEST:
        case STROLLERS_DETAIL_FETCH_FAILURE: {
            const key = slugAndVariantId(action);
            return tupdate(state, {
                [key]: tupdate(state[key] || initialStrollerDetailState, {
                    loadingState: loadingState(undefined, action),
                    errorMessage: errorMessage(undefined, action),
                }),
            });
        }
        case STROLLERS_DETAIL_FETCH_SUCCESS: {
            const key = slugAndVariantId(action);
            const detailState = state[key] || initialStrollerDetailState;
            return tupdate(state, {
                [key]: tupdate(detailState, {
                    loadingState: loadingState(undefined, action),
                    errorMessage: errorMessage(undefined, action),
                    linkedObjects: action.response.result.linkedObjects,
                    articleIds: action.response.result.articles,
                    eshopProductsIds: action.response.result.eshopProducts,
                    strollerQuestionIds: action.response.result.strollerQuestions,
                    patternsIds: action.response.result.patterns,
                    strollerReviewIds: action.response.result.strollerReviews,
                    strollerAlikeStrollerIds: action.response.result.strollerAlikeStrollers,
                    strollerPendingReviewIds: action.response.result.strollerPendingReviews,
                    strollerId: action.response.result.stroller,
                    strollersOfThisBrandIds: action.response.result.strollersOfThisBrand,
                    oldProductsCount: action.response.result.oldProductsCount,
                    cheapestOldProductId: action.response.result.cheapestOldProduct,
                    counsellingId: action.response.result.counselling,
                    variantsIds: action.response.result.variantsIds,
                    variantsOptions: action.response.result.variantsOptions,
                    baseVariantId: action.response.result.baseVariantId,
                    strollerPhotosIds: action.response.result.strollerPhotos,
                    variantsPhotos: action.response.result.variantsPhotos,
                    descriptionAST: action.response.result.descriptionAST,
                    descriptionASTAdditionalData:
                        action.response.result.descriptionASTAdditionalData,
                    descriptionPhotos: action.response.result.descriptionPhotos,
                    eshopProductsWidgetIds: action.response.result.eshopProductsWidget,

                    similarIds: uniq([
                        ...detailState.similarIds,
                        ...action.response.result.similars,
                    ]),
                    hasMoreSimilars: action.response.result.hasMoreSimilars,
                    countOfSimilars: action.response.result.countOfSimilars,
                    loadMoreSimilarsLoadingState: loadingState(undefined, action),
                    onProfile: action.response.result.onProfile,
                    buyingGuideId: action.response.result.buyingGuide,
                    allStrollerPhotosIds: action.response.result.allStrollerPhotos,
                    relatedStrollersPreviewPhotos:
                        action.response.result.relatedStrollersPreviewPhotos,
                }),
            });
        }

        case STROLLERS_DETAIL_LOAD_MORE_SIMILARS_REQUEST:
        case STROLLERS_DETAIL_LOAD_MORE_SIMILARS_FAILURE: {
            const key = slugAndVariantId(action);
            const detailState = state[key] || initialStrollerDetailState;
            return tupdate(state, {
                [key]: tupdate(detailState, {
                    loadMoreSimilarsLoadingState: loadingState(undefined, action),
                }),
            });
        }
        case STROLLERS_DETAIL_LOAD_MORE_SIMILARS_SUCCESS: {
            const key = slugAndVariantId(action);
            const detailState = state[key] || initialStrollerDetailState;
            return tupdate(state, {
                [key]: tupdate(detailState, {
                    loadMoreSimilarsLoadingState: loadingState(undefined, action),
                    countOfSimilars: action.response.result.countOfSimilars,
                    hasMoreSimilars: action.response.result.hasMoreSimilars,
                    similarIds: uniq([
                        ...detailState.similarIds,
                        ...action.response.result.similars,
                    ]),
                    allStrollerPhotosIds: uniq([
                        ...detailState.allStrollerPhotosIds,
                        ...("response" in action ? action.response.result.strollersPhotos : []),
                    ]),
                    relatedStrollersPreviewPhotos: tupdate(
                        detailState.relatedStrollersPreviewPhotos,
                        "response" in action ? action.response.result.strollersPreviewPhotoMap : {},
                    ),
                }),
            });
        }

        case STROLLERS_DETAIL_ADD_TO_PROFILE_API_REQUEST:
        case STROLLERS_DETAIL_ADD_TO_PROFILE_API_FAILURE: {
            const key = slugAndVariantId(action);
            const detailState = state[key] || initialStrollerDetailState;
            return tupdate(state, {
                [key]: tupdate(detailState, {
                    addingToProfileLoadingState: loadingState(undefined, action),
                }),
            });
        }
        case STROLLERS_DETAIL_ADD_TO_PROFILE_API_SUCCESS: {
            const key = slugAndVariantId(action);
            const detailState = state[key] || initialStrollerDetailState;
            return tupdate(state, {
                [key]: tupdate(detailState, {
                    addingToProfileLoadingState: loadingState(undefined, action),
                    onProfile: true,
                }),
            });
        }

        case STROLLERS_DETAIL_REMOVE_FROM_PROFILE_API_REQUEST:
        case STROLLERS_DETAIL_REMOVE_FROM_PROFILE_API_FAILURE: {
            const key = slugAndVariantId(action);
            const detailState = state[key] || initialStrollerDetailState;
            return tupdate(state, {
                [key]: tupdate(detailState, {
                    removingFromProfileLoadingState: loadingState(undefined, action),
                }),
            });
        }
        case STROLLERS_DETAIL_REMOVE_FROM_PROFILE_API_SUCCESS: {
            const key = slugAndVariantId(action);
            const detailState = state[key] || initialStrollerDetailState;
            return tupdate(state, {
                [key]: tupdate(detailState, {
                    removingFromProfileLoadingState: loadingState(undefined, action),
                    onProfile: false,
                }),
            });
        }

        case STROLLERS_DETAIL_CHANGE_VARIANT_FEATURE_VALUE_SUCCESS:
        case STROLLERS_DETAIL_CHANGE_VARIANT_FEATURE_VALUE_TRIGGER:
        default:
            return state;
    }
};

export default strollersDetailReducer;
